import {WizardRef} from 'platform/components';

import {useCallback, useEffect, useRef} from 'react';
import {useParams, createPath, useLocation} from 'react-router-dom';

import {assignSafely, getSafely, useQueryState} from 'shared/web';

import {focusFirstInputInTabPanel} from '../../ui/utils/focusFirstInputInTabPanel';

/**
 * Configuration for tab routing in wizard forms
 */
export interface WizardTabRoutingConfig {
  /**
   * Map of tab names to their corresponding step indices
   * @example { 'basic-information': 0, 'vehicle-data': 1, 'features': 2 }
   */
  tabStepMap: Record<string, number>;

  /**
   * The URL pattern for tab routing
   * @example '/vehicles/:id/edit/v2/:tab'
   */
  tabRoutePattern?: string;

  /**
   * Whether to use query params instead of path params
   * @default false
   */
  useQueryParams?: boolean;

  /**
   * The query param key when using query params
   * @default 'tab'
   */
  queryParamKey?: string;

  /**
   * Default tab when no tab is specified in URL
   */
  defaultTab?: string;
}

/**
 * Hook for handling tab routing in wizard forms with bidirectional URL synchronization
 *
 * This hook handles both URL → wizard step synchronization and wizard step → URL synchronization.
 * When the URL changes, the wizard step is updated. When the wizard step changes, the URL is updated.
 *
 * @param config Configuration object for tab routing
 * @returns Object containing wizardRef and onStepChange callback
 *
 * @example
 * ```tsx
 * function EditVehicleForm() {
 *   const {wizardRef, onStepChange} = useWizardTabRouting({
 *     tabStepMap: {
 *       'basic-information': 0,
 *       'vehicle-data': 1,
 *       'features': 2,
 *     },
 *     tabRoutePattern: '/vehicles/:id/edit/v2/:tab',
 *     defaultTab: 'basic-information',
 *   });
 *
 *   return (
 *     <Wizard
 *       ref={wizardRef}
 *       orientation="horizontal"
 *       steps={wizardSteps}
 *       onStepChange={onStepChange}
 *     />
 *   );
 * }
 * ```
 */
export function useWizardTabRouting(config: WizardTabRoutingConfig) {
  const {
    tabStepMap,
    tabRoutePattern,
    useQueryParams = false,
    queryParamKey = 'tab',
    defaultTab,
  } = config;

  const params = useParams();
  const location = useLocation();
  const [queryTab, setQueryTab] = useQueryState(queryParamKey);
  const wizardRef = useRef<WizardRef>(null);

  // Track when URL updates are initiated by wizard step changes to prevent race conditions
  const isUpdatingFromWizard = useRef(false);

  // Get the current tab from URL (either path param or query param)
  const currentTab = useQueryParams
    ? queryTab || defaultTab || null
    : (params.tab as string) || defaultTab || null;

  // Get the reverse mapping (step index to tab name)
  const stepTabMap: Record<number, string> = {};
  Object.entries(tabStepMap).forEach(([tab, step]) => {
    assignSafely(stepTabMap, step, tab);
  });

  // Function to update URL when step changes
  const updateURLForStep = useCallback(
    (stepIndex: number) => {
      const tabName = getSafely(stepTabMap, stepIndex);

      if (!tabName) {
        return;
      }

      // Mark that we're updating URL from wizard to prevent race condition
      isUpdatingFromWizard.current = true;

      // Update URL based on routing method
      if (useQueryParams) {
        setQueryTab(tabName);
      } else if (tabRoutePattern) {
        const newPathname = tabRoutePattern.replace(':tab', tabName);
        let finalPathname = newPathname;
        Object.entries(params).forEach(([key, value]) => {
          if (key !== 'tab' && value) {
            finalPathname = finalPathname.replace(`:${key}`, encodeURIComponent(value));
          }
        });

        // Create new location object preserving search and hash
        const newLocation = {
          pathname: finalPathname,
          search: location.search,
          hash: location.hash,
        };

        const newUrl = createPath(newLocation);
        window.history.replaceState(null, '', newUrl);

        // Force React Router to re-parse the URL by dispatching a popstate event
        window.dispatchEvent(new PopStateEvent('popstate'));
      }

      // Reset the flag after a microtask to allow URL changes to propagate
      Promise.resolve().then(() => {
        isUpdatingFromWizard.current = false;
      });
    },
    [stepTabMap, useQueryParams, tabRoutePattern, params, location, setQueryTab]
  );

  // Callback for when wizard step changes
  const onStepChange = useCallback(
    (stepIndex: number) => {
      updateURLForStep(stepIndex);

      // Auto-focus first input after step changes
      focusFirstInputInTabPanel();
    },
    [updateURLForStep]
  );

  // Initialize the wizard step based on the URL tab
  useEffect(() => {
    // Skip if the URL change was initiated by wizard step change to prevent race condition
    if (isUpdatingFromWizard.current) {
      return;
    }

    const desiredTab =
      (currentTab && currentTab in tabStepMap && currentTab) ||
      (defaultTab && defaultTab in tabStepMap && defaultTab) ||
      null;

    if (!desiredTab) {
      return;
    }

    const desiredStep = getSafely(tabStepMap, desiredTab) ?? 0;

    const applyStep = () => {
      if (wizardRef.current) {
        wizardRef.current.setCurrentStep(desiredStep);
        // Ensure focus moves into the active panel when URL dictates the tab
        focusFirstInputInTabPanel();
        return true;
      }
      return false;
    };

    // Apply immediately if ref is ready; otherwise retry on the next frame once
    if (!applyStep()) {
      const rafId = requestAnimationFrame(() => {
        applyStep();
      });
      return () => cancelAnimationFrame(rafId);
    }
  }, [currentTab, tabStepMap, defaultTab]);

  // Initial focus is handled by the URL-sync effect above and by onStepChange.

  return {
    wizardRef,
    onStepChange,
    currentTab,
  };
}
